import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { addOrder } from '../store/slices/ordersSlice';
import { addClient } from '../store/slices/clientsSlice';
import { parseOrderEmail, ParsedOrderEmail, ParsedItem } from '../utils/orderParser';
import { findOrCreateClientByName } from '../utils/clientMatchingUtils';
import { Order } from '../types/order';
import { v4 as uuidv4 } from 'uuid';

export const OrderParser: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [parsedOrder, setParsedOrder] = useState<ParsedOrderEmail | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const dispatch = useAppDispatch();
  const clients = useAppSelector(state => state.clients.clients);
  const fillings = useAppSelector(state => state.recipes.fillings);

  const availableRecipes = fillings.map(f => ({ id: f.id, name: f.name }));

  const handleParse = () => {
    if (!inputText.trim()) {
      setParsedOrder(null);
      return;
    }

    const result = parseOrderEmail(inputText, availableRecipes);
    setParsedOrder(result);
    setIsEditing(false);
  };

  const handleClear = () => {
    setInputText('');
    setParsedOrder(null);
    setIsEditing(false);
  };

  const handleCreateOrder = () => {
    if (!parsedOrder) return;

    // Validate required fields
    if (parsedOrder.errors.length > 0) {
      alert(`Please fix the following errors:\n${parsedOrder.errors.join('\n')}`);
      return;
    }

    // Check that all items have matched recipes
    const unmatchedItems = parsedOrder.items.filter(item => !item.recipeId);
    if (unmatchedItems.length > 0) {
      alert(
        `The following items could not be matched to recipes:\n${unmatchedItems.map(i => `- ${i.recipeName}`).join('\n')}\n\nPlease edit the parsed data or add these recipes to your system first.`
      );
      return;
    }

    // Find or create client
    const { clientId, client, isNewClient } = findOrCreateClientByName(parsedOrder.customer.name, clients, {
      customerName: parsedOrder.customer.name,
      customerType: 'Individual',
      email: parsedOrder.customer.email,
      phone: parsedOrder.customer.phone,
      streetAddress: parsedOrder.customer.streetAddress,
      addressLine2: parsedOrder.customer.addressLine2,
      city: parsedOrder.customer.city,
      province: parsedOrder.customer.province,
      postalCode: parsedOrder.customer.postalCode
    });

    // Add client if new
    if (isNewClient) {
      dispatch(addClient(client));
      const clientName = client.type === 'individual' ? client.name : client.retailerName;
      console.log(`[OrderParser] Created new client: ${clientName} (${client.id})`);
    }

    // Create order
    const newOrder: Order = {
      id: uuidv4(),
      customerName: parsedOrder.customer.name,
      customerType: 'Individual',
      email: parsedOrder.customer.email,
      phone: parsedOrder.customer.phone,
      streetAddress: parsedOrder.customer.streetAddress,
      addressLine2: parsedOrder.customer.addressLine2,
      city: parsedOrder.customer.city,
      province: parsedOrder.customer.province,
      postalCode: parsedOrder.customer.postalCode,
      orderDate: new Date().toISOString().split('T')[0],
      deliveryType: parsedOrder.deliveryType,
      items: parsedOrder.items.map(item => ({
        id: uuidv4(),
        recipeId: item.recipeId,
        recipeName: item.matchedRecipe?.name || item.recipeName,
        quantity: item.quantity
      })),
      status: 'Pending',
      isFinalized: false,
      clientId
    };

    dispatch(addOrder(newOrder));

    // Show success and clear form
    alert('Order created successfully!');
    handleClear();
  };

  const getConfidenceIcon = (confidence: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high':
        return '✓';
      case 'medium':
        return '⚠';
      case 'low':
        return '✗';
    }
  };

  const getConfidenceColor = (confidence: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high':
        return '#28a745';
      case 'medium':
        return '#ffc107';
      case 'low':
        return '#dc3545';
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Order Parser</h1>
        <p style={styles.subtitle}>Parse order information from email body text</p>
      </header>

      <div style={styles.section}>
        <label style={styles.label}>Paste Order Email</label>
        <textarea
          style={styles.textarea}
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          placeholder={`Paste email text here...

Example format:
Your Order
     3 dozen mom's original beef and onion
     1 dozen farmer sausage and cheese
Name
     John Doe
Email
     john@example.com
Phone
     (204) 123-4567
Address
     123 Main St
     Winnipeg, Manitoba R3T 0C8
Canada`}
        />
        <p style={styles.helpText}>
          Paste the email body text containing order details in the format: Your Order → Name → Email → Phone → Address
          → Canada
        </p>
      </div>

      <div style={styles.buttonGroup}>
        <button style={styles.parseButton} onClick={handleParse}>
          Parse Order
        </button>
        <button style={styles.clearButton} onClick={handleClear}>
          Clear
        </button>
      </div>

      {parsedOrder && (
        <>
          <hr style={styles.divider} />

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Parsed Results</h2>

            {parsedOrder.errors.length > 0 && (
              <div style={styles.errorBox}>
                <strong>Errors:</strong>
                <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                  {parsedOrder.errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={styles.resultsContainer}>
              <h3 style={styles.subsectionTitle}>Customer Information</h3>
              <div style={styles.infoGrid}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Name:</span>
                  <span style={styles.infoValue}>{parsedOrder.customer.name || '(missing)'}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Email:</span>
                  <span style={styles.infoValue}>{parsedOrder.customer.email || '(missing)'}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Phone:</span>
                  <span style={styles.infoValue}>{parsedOrder.customer.phone || '(missing)'}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Address:</span>
                  <span style={styles.infoValue}>
                    {parsedOrder.customer.streetAddress || '(missing)'}
                    {parsedOrder.customer.city && (
                      <>
                        <br />
                        {parsedOrder.customer.city}, {parsedOrder.customer.province} {parsedOrder.customer.postalCode}
                      </>
                    )}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Delivery Type:</span>
                  <span style={styles.infoValue}>{parsedOrder.deliveryType}</span>
                </div>
              </div>

              <h3 style={styles.subsectionTitle}>Order Items ({parsedOrder.items.length})</h3>
              {parsedOrder.items.length === 0 ? (
                <p style={styles.emptyState}>No order items found</p>
              ) : (
                <ul style={styles.itemsList}>
                  {parsedOrder.items.map((item, idx) => (
                    <li key={idx} style={styles.itemRow}>
                      <span
                        style={{
                          ...styles.confidenceIcon,
                          color: getConfidenceColor(item.confidence)
                        }}
                      >
                        {getConfidenceIcon(item.confidence)}
                      </span>
                      <span style={styles.itemText}>
                        <strong>{item.matchedRecipe?.name || item.recipeName}</strong> × {item.quantity} dozen
                        {item.matchedRecipe && item.confidence !== 'high' && (
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                            (matched from: "{item.recipeName}")
                          </span>
                        )}
                        {!item.matchedRecipe && (
                          <span style={{ fontSize: '12px', color: '#dc3545', marginLeft: '8px' }}>
                            (no match found)
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <div style={styles.legend}>
                <span style={{ color: '#28a745' }}>✓ High confidence</span>
                <span style={{ color: '#ffc107', marginLeft: '16px' }}>⚠ Medium confidence</span>
                <span style={{ color: '#dc3545', marginLeft: '16px' }}>✗ No match / Low confidence</span>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={{
                  ...styles.createButton,
                  opacity: parsedOrder.errors.length > 0 ? 0.5 : 1,
                  cursor: parsedOrder.errors.length > 0 ? 'not-allowed' : 'pointer'
                }}
                onClick={handleCreateOrder}
                disabled={parsedOrder.errors.length > 0}
              >
                Create Order
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    marginBottom: '40px'
  },
  title: {
    fontSize: '2.5rem',
    color: '#333',
    margin: 0
  },
  subtitle: {
    color: '#666',
    fontSize: '1.1rem',
    marginTop: '8px'
  },
  section: {
    marginBottom: '30px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontFamily: 'monospace',
    border: '1px solid #ddd',
    borderRadius: '4px',
    resize: 'vertical',
    minHeight: '300px',
    boxSizing: 'border-box'
  },
  helpText: {
    fontSize: '13px',
    color: '#666',
    marginTop: '6px',
    fontStyle: 'italic'
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px'
  },
  parseButton: {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  clearButton: {
    padding: '10px 20px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  createButton: {
    padding: '10px 20px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  divider: {
    margin: '40px 0',
    border: 'none',
    borderTop: '2px solid #ddd'
  },
  sectionTitle: {
    fontSize: '2rem',
    color: '#333',
    marginBottom: '20px'
  },
  subsectionTitle: {
    fontSize: '1.3rem',
    color: '#333',
    marginTop: '24px',
    marginBottom: '12px'
  },
  errorBox: {
    padding: '12px',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    color: '#721c24',
    marginBottom: '20px'
  },
  resultsContainer: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '20px',
    backgroundColor: '#f9f9f9'
  },
  infoGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  infoRow: {
    display: 'flex',
    alignItems: 'flex-start'
  },
  infoLabel: {
    fontWeight: '600',
    color: '#555',
    minWidth: '120px'
  },
  infoValue: {
    color: '#333',
    flex: 1
  },
  itemsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  itemRow: {
    padding: '12px',
    marginBottom: '8px',
    backgroundColor: 'white',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center'
  },
  confidenceIcon: {
    fontSize: '20px',
    marginRight: '12px',
    fontWeight: 'bold'
  },
  itemText: {
    fontSize: '14px'
  },
  legend: {
    marginTop: '16px',
    fontSize: '13px',
    color: '#666'
  },
  emptyState: {
    textAlign: 'center',
    color: '#999',
    padding: '20px',
    fontSize: '14px'
  }
};
